import { Component, Input, Output, EventEmitter, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';            
import { FormsModule } from '@angular/forms';               
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgxDocViewerModule } from 'ngx-doc-viewer';        
import { TimeAgoOrDatePipe } from '../../pipe/time-ago-or-date.pipe'; 
import { fadeStagger, slideIn } from '../../app.animations'; 
import { CommunityService } from '../../../services/community.service';
import { ToastrService } from 'ngx-toastr';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-reply-item',
  standalone: true,
  templateUrl: './reply-item.component.html',
  styleUrls: ['./reply-item.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    NgxDocViewerModule,
    TimeAgoOrDatePipe   
  ],
  animations: [fadeStagger, slideIn]
})
export class ReplyItemComponent {
  @Input() reply: any;
  @Input() isMobile = false;
  @Input() communityID!: number;
  @Input() availabiltyDurations: any;


  @Output() submitReplyToReply = new EventEmitter<{
    parentId: number;
    replyDepth: number;
    content: string;
    files: File[];
  }>();

  @ViewChild('imageModal') imageModalTemplate!: TemplateRef<any>;
  imageModalRef?: NgbModalRef;
  @ViewChild('profileModal') profileModal!: TemplateRef<any>;


  expanded = false;
  replying = false;
  showReplies = true;
  replyContent = '';
  selectedFiles: File[] = [];
  imagePreviews: string[] = [];
  imageError = '';
  isGeneratingPreview = false;
  submitting = false;

  imageViewer: { list: string[]; current: string; index: number } | null = null;
  selectedUser: any;
  @Input() currentUserId!: number;
  // resumeViewerUrl: any;
  


  constructor(
    private modal: NgbModal,
    private communityService: CommunityService,
    private toastr: ToastrService,
    private cdRef: ChangeDetectorRef
  ) {}

  toggleExpand(): void {
    this.expanded = !this.expanded;
  }

  getPaddingDepth(depth: number): number {
    return Math.min(depth, 5);
  }

  openProfile(userId: number) {
    this.communityService.getUserPublicProfile(this.communityID, userId).subscribe({
      next: (res) => {
        this.selectedUser = res.data.data;
  
   
        // this.resumeViewerUrl = this.selectedUser?.resume_url || null;
  
        this.modal.open(this.profileModal, {
          size: 'lg',
          backdrop: 'static',
          keyboard: false
        });
        
      },
      error: () => {
        this.toastr.error('Failed to load user profile.');
      },
    });
  }
  

  onFileChange(event: any): void {
    const files = Array.from(event.target.files) as File[];
    const availableSlots = 5 - this.selectedFiles.length;

    if (files.length > availableSlots) {
      this.imageError = `You can upload only ${availableSlots} more image(s).`;
      return;
    }

    this.isGeneratingPreview = true;
    let processed = 0;

    for (const file of files) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > 4096 || img.height > 4096) {
            this.imageError = `${file.name} exceeds max resolution`;
            return;
          }

          const finalize = (finalFile: File) => {
            this.selectedFiles.push(finalFile);
            this.createPreview(finalFile);
            processed++;
            if (processed === files.length) this.isGeneratingPreview = false;
          };

          if (img.width > 1024) {
            const canvas = document.createElement('canvas');
            const ratio = img.height / img.width;
            canvas.width = 1024;
            canvas.height = 1024 * ratio;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              if (blob) {
                const resized = new File([blob], file.name, { type: file.type });
                finalize(resized);
              }
            }, file.type);
          } else {
            finalize(file);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }

    event.target.value = '';
  }

  createPreview(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imagePreviews.push(e.target.result);
      this.cdRef.detectChanges();  
    };
    reader.readAsDataURL(file);
  }

  removeImage(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.imagePreviews.splice(index, 1);
  }

  submitReply(): void {
    if (!this.replyContent.trim() && this.selectedFiles.length === 0) return;

    this.submitting = true;
    this.submitReplyToReply.emit({
      parentId: this.reply.id,
      replyDepth: this.reply.reply_depth,
      content: this.replyContent,
      files: this.selectedFiles,
    });

    this.resetForm();
  }

  resetForm(): void {
    this.replyContent = '';
    this.replying = false;
    this.selectedFiles = [];
    this.imagePreviews = [];
    this.imageError = '';
    this.submitting = false;
  }

  sanitizeAndLinkify(content: string, limit?: number): string {
    const truncated = limit ? content.slice(0, limit) : content;
    return truncated.replace(
      /((https?:\/\/|www\.)[^\s]+)/g,
      (url) => {
        const link = url.startsWith('http') ? url : `https://${url}`;
        return `<a href="${link}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      }
    );
  }

  openImageViewer(images: any[], index: number): void {
    this.imageViewer = {
      list: images.map(i => i.file_url || i.thumbnail_url),
      current: images[index].file_url || images[index].thumbnail_url,
      index,
    };
    this.imageModalRef = this.modal.open(this.imageModalTemplate, { size: 'lg', centered: true });
  }

  hasNext(): boolean {
    return !!this.imageViewer && this.imageViewer.index < this.imageViewer.list.length - 1;
  }

  hasPrev(): boolean {
    return !!this.imageViewer && this.imageViewer.index > 0;
  }

  nextImage(): void {
    if (this.hasNext()) {
      this.imageViewer!.index += 1;
      this.imageViewer!.current = this.imageViewer!.list[this.imageViewer!.index];
    }
  }

  prevImage(): void {
    if (this.hasPrev()) {
      this.imageViewer!.index -= 1;
      this.imageViewer!.current = this.imageViewer!.list[this.imageViewer!.index];
    }
  }

  getDurationLabel(id: number | null): string {
    // 8 means "Unknown"/no label, skip it
    if (!id || id === 8) return '';
  
    const found = this.availabiltyDurations?.find(
      (d: { duration_id: number }) => d.duration_id === id
    );
    return found ? found.value : '';
  }
  
  getAvailabilityStatus(av: { start_at: string | null; duration_id: number | null }): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    const start = av?.start_at ? new Date(av.start_at) : null;
    if (start) start.setHours(0, 0, 0, 0); // Normalize to local date only
  
    const rawDurationText = av?.duration_id ? this.getDurationLabel(av.duration_id) : '';
    const durationText = rawDurationText && rawDurationText !== 'Unknown' ? rawDurationText : null;
  
    if (!start || start <= today) {
      return durationText ? `Available Now (${durationText})` : 'Available Now';
    }
  
    const formattedDate = start.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  
    return durationText
      ? `Available From ${formattedDate} (${durationText})`
      : `Available From ${formattedDate}`;
  }
  
  getAvailabilityBadgeClass(av: { start_at: string | null; duration_id: number | null }): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    const startAt = av?.start_at ? new Date(av.start_at) : null;
    if (startAt) startAt.setHours(0, 0, 0, 0); // Normalize to local date only
  
    if (!startAt || startAt <= today) {
      return 'bg-success text-white';
    }
  
    return 'bg-warning text-dark';
  }
  
  
  

}
